import { useState, useEffect } from "react";
import ApiService from "../../service/ApiService";
import { apiPaths } from "../../service/apiPaths";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";

const useUser = () => {
  const [userData, setUserData] = useState<any[]>([]);
  const [selectedUserView, setSelectedUserView] = useState<
    string | null
  >(null);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(
    null
  );
  const [loading, setLoading] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const navigate = useNavigate();

  // Debounced search effect
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      fetchData(searchQuery);
    }, 500); // 500ms debounce delay

    return () => clearTimeout(debounceTimer);
  }, [searchQuery]);

  const fetchData = async (search?: string) => {
    try {
      let endpoint = apiPaths.getUser;
      
      // Add search query parameter if provided
      if (search && search.trim().length > 0) {
        endpoint = `${apiPaths.getUser}?q=${encodeURIComponent(search.trim())}`;
      }

      const result = await ApiService({
        method: "GET",
        endpoint: endpoint,
      });
      setUserData(result.users || [])
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const formatDateToISODateString = (date: Date): string => {
    return date.toISOString().split("T")[0];
  };

  const handleDateFilter = async (fromDate: Date, toDate: Date) => {
    const formattedFromDate = formatDateToISODateString(fromDate);
    const formattedToDate = formatDateToISODateString(toDate);

    try {
      const result = await ApiService({
        method: "GET",
        endpoint: `${apiPaths.getUser}?fromDate=${formattedFromDate}&toDate=${formattedToDate}`,
      });
      setUserData(result);
    } catch (error) {
      console.error("Error fetching filtered data:", error);
    }
  };

  const handleView = (id: string) => {
    setSelectedUserView(id);
  };

  const handleEdit = (id: string) => {
    setSelectedUserId(id);
  };

  const handleDelete = async (id: string) => {
    setLoading(true);
    try {
      await ApiService({
        method: "DELETE",
        endpoint: `${apiPaths.deleteUser}/${id}`,
      });
      setUserData((prevData) =>
        prevData.filter((user) => user._id !== id)
    );
    toast.success("User deleted successfully");
    } catch (error) {
      console.error("Error deleting User:", error);
      toast.error("Error deleting User");
    } finally {
      setLoading(false);
    }
  };



  const handleDisable = async(id:string)=>{
     setLoading(true);
    try {
      await ApiService({
        method: "PUT",
        endpoint: `${apiPaths.disableUser}/${id}`,
      });

      setUserData((prevData) => {
        if (Array.isArray(prevData)) {
          return prevData.map(user => {
            if (user._id === id) {
              return {
                ...user,
                isDisabled: !user.isDisabled, // Toggle the status
                disabled: !user.disabled, // Also update if you use 'disabled' field
                status: user.isDisabled ? 'active' : 'disabled' // Update status field if used
              };
            }
            return user;
          });
        }
        return prevData;
      });
   
    toast.success("User Disabled successfully");
    } catch (error) {
      console.error("Error disabling User:", error);
      toast.error("Error disabling User");
    } finally {
      setLoading(false);
    }

  }

  const handleStatusUp = async (id: string, currentStatus: boolean) => {
    setLoading(true);
    const newStatus = currentStatus;
    console.log(newStatus);
    try {
      await ApiService({
        method: "PUT",
        endpoint: `${apiPaths.updateUser}/${id}`,
        data: { isActive: newStatus },
      });
      setUserData((prevData) =>
        prevData.map((user) =>
          user._id === id ? { ...user, isActive: newStatus } : user
        )
      );
    } catch (error) {
      console.error("Error updating user status:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    navigate("/add-user");
  };
  return {
    selectedUserView,
    selectedUserId,
    userData,
    loading,
    searchQuery,
    setSearchQuery,
    handleView,
    handleEdit,
    handleDelete,
    handleStatusUp,
    handleDateFilter,
    handleAdd,
    handleDisable
  };
};

export default useUser;
